/**
 * REPORT CONSISTENCY ENGINE — canonical Report Summary.
 *
 * One immutable object that owns EVERY report-wide statistic in the Claim
 * Intelligence Package™. Chapters, tables, KPI cards, graphs, the executive
 * summary, the sidebar, the PDF export, dashboard widgets and the negotiation
 * report must all read these values. No component may recompute or hardcode a
 * report-wide statistic.
 *
 * Financial values continue to originate from `buildReportFromBundle()`
 * (canonical financial source of truth) — this module aggregates, it never
 * re-derives recovery math.
 *
 * The returned object is deep-frozen: any attempt to mutate it throws in
 * strict mode, which is how we guarantee "single immutable source of truth".
 */

import type {
  AIFindings,
  FindingItem,
  InvoiceData,
  LegalResearch,
  MarketComparison,
  MarketResearch,
  MarketResearchSource,
  Review,
} from "@/lib/db/models";
import {
  buildReportFromBundle,
  extractInvoiceReportFields,
  type Report,
} from "@/lib/report";
import { isRegulatoryAuthority } from "@/lib/legal/topic-classifier";
import { buildChargeInventory, normalizeChargeKey } from "@/lib/report/charge-inventory";
import { resolveChargeBenchmarkStatuses } from "@/lib/report/charge-benchmark-status";
import { buildMarketRows, type MarketRow } from "@/lib/report/market-evidence";
import { resolveStorageTimeline, type StorageDaysBasis } from "@/lib/report/storage-timeline";


/* ─────────────────────────── Types ─────────────────────────── */

export interface ReportSummaryInputs {
  review: Review | null;
  invoiceData?: InvoiceData | null;
  findings?: AIFindings | null;
  findingItems?: FindingItem[];
  legalResearch?: LegalResearch[];
  marketComparisons?: MarketComparison[];
  marketResearch?: MarketResearch[];
  marketResearchSources?: MarketResearchSource[];
}

export interface ReportSummaryCounts {
  /** Every distinct billed charge on the invoice (charge inventory). */
  totalInvoiceCharges: number;
  /** Charges that carry usable market evidence (benchmark or researched). */
  marketSupportedCharges: number;
  chargesAboveMarket: number;
  chargesWithinMarket: number;
  chargesBelowMarket: number;
  /** Charges still awaiting comparable pricing. */
  pendingComparablePricing: number;
  /** Charges priced against an official / regional published rate. */
  publishedRateCharges: number;
  /** Accepted market comparables backing the analysis. */
  supportingMarketSources: number;
  /** All benchmark sources referenced (accepted comparables + benchmark rows). */
  totalBenchmarkSources: number;
  applicableLaws: number;
  applicableRegulations: number;
  /** Total authorities = laws + regulations. */
  applicableAuthorities: number;
  findingsCount: number;
}

export interface ReportSummaryFinancials {
  invoiceTotal: number;
  /**
   * Sum of every billed charge in the charge inventory. This is the scope the
   * charge-level tables actually review, and it is NOT the invoice total when
   * the invoice carries amounts that never reached a line item.
   */
  chargesReviewedTotal: number;
  /** Invoice Total − Charges Reviewed Subtotal. Zero on a fully itemized invoice. */
  invoiceUnreviewedVariance: number;
  /** Vendor dollars covered by market evidence. */
  vendorTotalWithinMarketScope: number;
  /** Estimated Fair Payment — the amount supported by evidence. */
  supportedAmount: number;
  /** Canonical recovery figure. */
  potentialSavings: number;
  /** Recovery attributable to benchmark variance. */
  marketBasedRecovery: number;
  /** Recovery attributable to non-market (documented) grounds. */
  nonMarketRecovery: number;
  /** Settlement goal = supported amount. */
  settlementGoal: number;
  /** Estimated Fair Payment band (low / high). */
  fairPaymentLow: number;
  fairPaymentHigh: number;
  /** Total Supported Recovery band (low / high). */
  recoveryLow: number;
  recoveryHigh: number;
  /** Recommended payment = supported amount. */
  recommendedPayment: number;
}

export interface ReportSummaryPercentages {
  /** Recovery as a share of the invoice total. */
  recoveryPct: number;
  /** Supported amount as a share of the invoice total. */
  supportedPct: number;
  /** Share of charges with market evidence. */
  marketCoveragePct: number;
  /** Share of charges above market. */
  aboveMarketPct: number;
  /** Analysis confidence, 0–100. */
  confidencePct: number;
}

export interface ReportSummaryVehicle {
  vin: string | null;
  licensePlate: string | null;
  year: string | null;
  make: string | null;
  model: string | null;
  color: string | null;
  description: string | null;
}

export interface ReportSummaryDates {
  invoiceDate: string | null;
  towDate: string | null;
  /** Canonical storage period start (same source as `storageDays`). */
  storageStart: string | null;
  /** Canonical storage period end (same source as `storageDays`). */
  storageEnd: string | null;
  /** How the canonical storage duration was established. */
  storageDaysBasis: StorageDaysBasis;
  releaseDate: string | null;
  reportGeneratedAt: string;
}


export interface ReportSummary {
  reviewId: string | null;
  fileNumber: string | null;
  vendor: string | null;
  invoiceNumber: string | null;
  invoiceType: string | null;
  state: string | null;
  zip: string | null;
  storageDays: number | null;
  counts: ReportSummaryCounts;
  financials: ReportSummaryFinancials;
  percentages: ReportSummaryPercentages;
  vehicle: ReportSummaryVehicle;
  dates: ReportSummaryDates;
  /** Flat field map used by the Global Consistency Certification. */
  fields: Readonly<Record<string, number | string | null>>;
  /** Canonical report shape financials came from. */
  canonicalReport: Report;
  builtAt: string;
  engineVersion: number;
}

export const REPORT_SUMMARY_ENGINE_VERSION = 1;

/* ─────────────────────────── Helpers ─────────────────────────── */

const ZERO_REPORT: Report = {
  invoiceTotal: 0,
  fairRange: [0, 0],
  potentialSavings: [0, 0],
  savingsPct: [0, 0],
  estimatedSavings: 0,
  strength: "Low",
  confidence: 0,
  vendor: "",
  invoiceDate: "",
  invoiceType: "",
  zip: "",
  state: "",
  findings: [],
  laws: [],
  violations: [],
  timeline: [],
  nextSteps: [],
};

// Statute vs. regulation is decided by the ONE canonical classifier
// (`isRegulatoryAuthority`). The Report Summary must never run its own
// regex: a local classifier keyed on the free-text topic mis-filed
// legislative statutes (e.g. "Tex. Occ. Code § 2308", topic "Towing and
// Storage Regulation") as regulations, so the summary's applicableLaws /
// applicableRegulations counts contradicted the chapters that route the
// same rows. SAB-002.
function isRegulation(row: LegalResearch): boolean {
  return isRegulatoryAuthority(row);
}

function pct(part: number, whole: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return 0;
  return Math.round((part / whole) * 100);
}

function money(n: number | null | undefined): number {
  return typeof n === "number" && Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

export function deepFreeze<T>(value: T): T {
  if (value && (typeof value === "object" || typeof value === "function")) {
    for (const key of Object.getOwnPropertyNames(value)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
    Object.freeze(value);
  }
  return value;
}

/** Charges that carry usable market evidence, keyed by normalized family. */
function researchedKeys(rows: MarketResearch[]): {
  supported: Set<string>;
  published: Set<string>;
  acceptedSources: number;
} {
  const supported = new Set<string>();
  const published = new Set<string>();
  let acceptedSources = 0;
  for (const r of rows) {
    const key = normalizeChargeKey(r.charge_key || r.charge_description);
    const accepted = typeof r.accepted_source_count === "number" ? r.accepted_source_count : 0;
    acceptedSources += Math.max(0, accepted);
    if (r.status === "complete" && accepted > 0) supported.add(key);
    if (
      r.source_classification === "official_published_rate" ||
      r.source_classification === "regional_published_pricing"
    ) {
      published.add(key);
    }
  }
  return { supported, published, acceptedSources };
}

/* ─────────────────────────── Builder ─────────────────────────── */

export function buildReportSummary(inputs: ReportSummaryInputs): ReportSummary {
  const review = inputs.review ?? null;
  const findingItems = inputs.findingItems ?? [];
  const legalResearch = inputs.legalResearch ?? [];
  const marketComparisons = inputs.marketComparisons ?? [];
  const marketResearch = inputs.marketResearch ?? [];
  const marketResearchSources = inputs.marketResearchSources ?? [];

  const invoiceFields = extractInvoiceReportFields(inputs.invoiceData ?? null, review);
  const canonicalReport = buildReportFromBundle(
    {
      review,
      invoiceData: inputs.invoiceData ?? null,
      findings: inputs.findings ?? null,
      findingItems,
      legalResearch,
      marketResearch,
      marketComparisons,
    },
    ZERO_REPORT,
  );


  const { canonical: charges } = buildChargeInventory(invoiceFields.lineItems);
  const marketRows: MarketRow[] = buildMarketRows(marketComparisons, findingItems);
  const research = researchedKeys(marketResearch);

  /* CANONICAL CHARGE-LEVEL COVERAGE.
     Every chapter-visible benchmark status comes from the one resolver, so
     Chapter 01 and Chapter 02 can never disagree about how many charges are
     market supported. */
  const resolution = resolveChargeBenchmarkStatuses({
    lineItems: invoiceFields.lineItems,
    marketComparisons,
    marketResearch,
  });

  const above = resolution.counts.materiallyAbove + resolution.counts.above;
  const within = resolution.counts.within;
  const below = resolution.counts.below;
  const supported = resolution.counts.supported;
  const vendorInScope = resolution.supportedVendorTotal;
  const publishedCount = charges.filter((c) => research.published.has(c.normalized)).length;

  const totalCharges = charges.length;
  const pending = resolution.counts.pending;

  const invoiceTotal = money(canonicalReport.invoiceTotal || invoiceFields.totalDue || 0);
  const potentialSavings = money(canonicalReport.estimatedSavings);
  const supportedAmount = money(Math.max(0, invoiceTotal - potentialSavings));
  // Scope actually reviewed charge-by-charge. Owned here so no chapter table
  // has to re-add the line items itself.
  const chargesReviewedTotal = money(charges.reduce((s, ch) => s + money(ch.amount), 0));
  const invoiceUnreviewedVariance = money(invoiceTotal - chargesReviewedTotal);

  const benchmarkRecoveryRaw = marketRows
    .filter((r) => r.savingsBasis === "benchmark")
    .reduce((s, r) => s + (r.savingsHigh || 0), 0);
  const nonMarketRecoveryRaw = marketRows
    .filter((r) => r.savingsBasis === "non-market")
    .reduce((s, r) => s + (r.savingsHigh || 0), 0);
  const evidenceTotal = benchmarkRecoveryRaw + nonMarketRecoveryRaw;
  // Split the CANONICAL recovery proportionally to the documented evidence, so
  // the two components always sum to the canonical figure exactly.
  const marketBasedRecovery =
    evidenceTotal > 0 ? money((benchmarkRecoveryRaw / evidenceTotal) * potentialSavings) : potentialSavings;
  const nonMarketRecovery = money(Math.max(0, potentialSavings - marketBasedRecovery));

  const laws = legalResearch.filter((l) => !isRegulation(l)).length;
  const regulations = legalResearch.length - laws;

  const acceptedSources =
    marketResearchSources.filter((s) => s.accepted).length || research.acceptedSources;
  const benchmarkSources = acceptedSources + marketComparisons.filter((c) => !!c.source).length;

  const extracted = (inputs.invoiceData?.extracted ?? {}) as Record<string, unknown>;
  const vehicleRaw = (extracted.vehicle ?? {}) as Record<string, unknown>;
  const vehicle: ReportSummaryVehicle = {
    vin: str(vehicleRaw.vin) ?? str(extracted.vin),
    licensePlate: str(vehicleRaw.license_plate) ?? str(extracted.license_plate),
    year: str(vehicleRaw.year) ?? str(extracted.vehicle_year),
    make: str(vehicleRaw.make) ?? str(extracted.vehicle_make),
    model: str(vehicleRaw.model) ?? str(extracted.vehicle_model),
    color: str(vehicleRaw.color) ?? str(extracted.vehicle_color),
    description: null,
  };
  vehicle.description =
    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || null;

  /* Storage duration is report-wide and canonical: it comes from the single
     storage timeline resolver, which always derives an inclusive day count
     from the strongest date evidence on the claim. A bare `storage_days`
     value on the invoice is only used when no date pair exists. */
  const storageTimeline = resolveStorageTimeline({
    extracted,
    lineItems: (inputs.invoiceData?.line_items ?? []) as Array<Record<string, unknown>>,
  });
  const storageDays =
    storageTimeline.days !== null && storageTimeline.days > 0 ? storageTimeline.days : null;


  const counts: ReportSummaryCounts = {
    totalInvoiceCharges: totalCharges,
    marketSupportedCharges: supported,
    chargesAboveMarket: above,
    chargesWithinMarket: within,
    chargesBelowMarket: below,
    pendingComparablePricing: pending,
    publishedRateCharges: publishedCount,
    supportingMarketSources: acceptedSources,
    totalBenchmarkSources: benchmarkSources,
    applicableLaws: laws,
    applicableRegulations: regulations,
    applicableAuthorities: legalResearch.length,
    findingsCount: findingItems.length,
  };

  const financials: ReportSummaryFinancials = {
    invoiceTotal,
    chargesReviewedTotal,
    invoiceUnreviewedVariance,
    vendorTotalWithinMarketScope: money(vendorInScope),
    supportedAmount,
    potentialSavings,
    marketBasedRecovery,
    nonMarketRecovery,
    settlementGoal: supportedAmount,
    fairPaymentLow: money(canonicalReport.fairRange[0]),
    fairPaymentHigh: money(canonicalReport.fairRange[1]),
    recoveryLow: money(canonicalReport.potentialSavings[0]),
    recoveryHigh: money(canonicalReport.potentialSavings[1]),
    recommendedPayment: supportedAmount,
  };

  const percentages: ReportSummaryPercentages = {
    recoveryPct: pct(potentialSavings, invoiceTotal),
    supportedPct: pct(supportedAmount, invoiceTotal),
    marketCoveragePct: pct(supported, totalCharges),
    aboveMarketPct: pct(above, totalCharges),
    confidencePct: canonicalReport.confidence,
  };

  const iso = (d: Date | null) => (d ? d.toISOString() : null);
  const dates: ReportSummaryDates = {
    invoiceDate: str(invoiceFields.invoiceDate) ?? null,
    towDate: str(extracted.tow_date) ?? null,
    // Start / End / Duration all come from the same canonical timeline.
    storageStart: iso(storageTimeline.start) ?? str(extracted.storage_start) ?? str(extracted.storage_start_date),
    storageEnd: iso(storageTimeline.end) ?? str(extracted.storage_end) ?? str(extracted.release_date),
    storageDaysBasis: storageTimeline.basis,
    releaseDate: str(extracted.release_date) ?? null,
    reportGeneratedAt: new Date().toISOString(),
  };


  const summary: ReportSummary = {
    reviewId: review?.id ?? null,
    fileNumber: review?.file_number ?? null,
    vendor: str(invoiceFields.vendorName),
    invoiceNumber: str(invoiceFields.invoiceNumber),
    invoiceType: str(invoiceFields.invoiceType),
    state: str(invoiceFields.state),
    zip: str(invoiceFields.zip),
    storageDays,
    counts,
    financials,
    percentages,
    vehicle,
    dates,
    fields: {},
    canonicalReport,
    builtAt: new Date().toISOString(),
    engineVersion: REPORT_SUMMARY_ENGINE_VERSION,
  };

  (summary as { fields: Record<string, number | string | null> }).fields = {
    ...Object.fromEntries(Object.entries(counts)),
    ...Object.fromEntries(Object.entries(financials)),
    ...Object.fromEntries(Object.entries(percentages)),
    fileNumber: summary.fileNumber,
    vendor: summary.vendor,
    invoiceNumber: summary.invoiceNumber,
    storageDays: summary.storageDays,
    vehicleDescription: vehicle.description,
    vin: vehicle.vin,
    invoiceDate: dates.invoiceDate,
    towDate: dates.towDate,
    storageStart: dates.storageStart,
    releaseDate: dates.releaseDate,
    strength: canonicalReport.strength,
  };

  return deepFreeze(summary);
}

/** Canonical list of the fields the certification engine checks. */
export type ReportSummaryField = keyof ReportSummary["fields"];

export const EMPTY_REPORT_SUMMARY: ReportSummary = buildReportSummary({ review: null });
